/**
 * ZenCircle "Check my points" modal — phone → SMS PIN → balance display
 * flow. Backed by Alpine IQ via /api/aiq/balance/{request-pin,balance}.
 *
 * Exported separately so the App-level mount (in App.js) can render the
 * global modal independent of the Loyalty page.
 */
import { useState } from "react";
import { X, ArrowRight, Sparkles, Wallet } from "lucide-react";
import { api } from "../../lib/api";
import { useLoyalty } from "../../lib/loyalty";
import { getSelectedStore } from "../../components/HeaderLocationPicker";

// Per-store placeholder so the format hint matches the customer's store of
// record (Queens vs Staten Island).
const STORE_PHONE_PLACEHOLDER = {
    "queens-long-island": "(718) 865-1464",
    "staten-island": "(718) 889-0240",
};

function PhoneInput({ value, onChange, disabled, autoFocus }) {
    const store = getSelectedStore();
    const placeholder =
        STORE_PHONE_PLACEHOLDER[store?.slug] || "(718) 865-1464";
    return (
        <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus={autoFocus}
            disabled={disabled}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid="loyalty-phone-input"
            className="w-full bg-zen-bg border border-zen-line px-4 py-3 text-base text-zen-ink placeholder:text-zen-ink-soft/60 focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15 disabled:opacity-50"
        />
    );
}

function PinInput({ value, onChange, disabled, autoFocus }) {
    return (
        <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            autoFocus={autoFocus}
            disabled={disabled}
            value={value}
            onChange={(e) =>
                onChange(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            placeholder="123456"
            data-testid="loyalty-pin-input"
            className="w-full bg-zen-bg border border-zen-line px-4 py-3 text-2xl font-serif tracking-[0.5em] text-center text-zen-ink placeholder:text-zen-ink-soft/40 focus:outline-none focus:border-zen-olive focus:ring-2 focus:ring-zen-olive/15 disabled:opacity-50"
        />
    );
}

function WalletPassButton({ phone }) {
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const send = async () => {
        setLoading(true);
        try {
            await api.post("/aiq/wallet-pass", { phone });
        } catch (e) {
            /* silent — wallet pass is a nice-to-have, not a blocker */
            if (typeof console !== "undefined")
                console.debug("[zen-loyalty] wallet-pass request failed:", e?.message);
        } finally {
            setSent(true);
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <p
                data-testid="wallet-pass-sent"
                className="mt-4 text-xs text-zen-olive inline-flex items-center gap-1.5"
            >
                <Wallet size={12} /> Wallet pass sent — check your texts.
            </p>
        );
    }
    return (
        <button
            onClick={send}
            disabled={loading}
            data-testid="wallet-pass-cta"
            className="mt-4 w-full border border-zen-line text-zen-ink px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-surface transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
            <Wallet size={13} />
            {loading ? "Sending…" : "Save to Apple / Google Wallet"}
        </button>
    );
}

export default function CheckPointsModal({ open, onClose }) {
    const [step, setStep] = useState("phone"); // phone | pin | result
    const [phone, setPhone] = useState("");
    const [pin, setPin] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const { setVerifiedBalance } = useLoyalty();

    const reset = () => {
        setStep("phone");
        setPhone("");
        setPin("");
        setError(null);
        setResult(null);
        setLoading(false);
    };

    // Axios baseURL already ends with `/api`.
    const requestPin = async () => {
        setLoading(true);
        setError(null);
        try {
            await api.post("/aiq/balance/request-pin", { phone });
            setStep("pin");
        } catch (e) {
            setError(
                e?.response?.data?.detail || "Couldn't send a code. Try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const verifyPin = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.post("/aiq/balance", { phone, pin });
            setResult(res.data);
            setStep("result");
            // Persist verified balance so the header pill + cart drawer
            // show the points across pages without re-prompting.
            if (res.data && res.data.enrolled !== false) {
                setVerifiedBalance({ points: res.data.points || 0, phone });
            }
        } catch (e) {
            const status = e?.response?.status;
            if (status === 429) {
                setStep("phone");
                setPin("");
            }
            setError(
                e?.response?.data?.detail ||
                    "Couldn't verify that code. Try again."
            );
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-zen-ink/40 backdrop-blur-sm p-0 sm:p-6"
            data-testid="loyalty-modal"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="relative bg-zen-bg w-full sm:max-w-md border border-zen-line shadow-[0_24px_60px_rgba(31,36,24,0.25)]">
                <button
                    onClick={onClose}
                    data-testid="loyalty-modal-close"
                    aria-label="Close"
                    className="absolute top-4 right-4 p-2 text-zen-ink-soft hover:text-zen-ink transition-colors"
                >
                    <X size={18} />
                </button>

                {step === "phone" && (
                    <div className="p-8 sm:p-10">
                        <p className="text-[11px] uppercase tracking-[0.28em] text-zen-olive mb-3 inline-flex items-center gap-1.5">
                            <Sparkles size={11} /> ZenCircle
                        </p>
                        <h2 className="font-serif font-light text-3xl text-zen-ink mb-2 leading-tight">
                            Check your points.
                        </h2>
                        <p className="text-sm text-zen-ink-soft mb-7 leading-relaxed">
                            Enter the phone you joined ZenCircle with. We&rsquo;ll
                            text you a 6-digit code.
                        </p>
                        <label className="block text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-2">
                            Mobile number
                        </label>
                        <PhoneInput
                            value={phone}
                            onChange={setPhone}
                            disabled={loading}
                            autoFocus
                        />
                        {error && (
                            <p
                                data-testid="loyalty-error"
                                className="text-sm text-red-700 mt-3"
                            >
                                {error}
                            </p>
                        )}
                        <button
                            onClick={requestPin}
                            disabled={
                                loading || phone.replace(/\D/g, "").length < 10
                            }
                            data-testid="loyalty-send-code"
                            className="mt-6 w-full bg-zen-ink text-zen-cream px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                        >
                            {loading ? "Sending…" : "Text me a code"}
                            {!loading && <ArrowRight size={14} />}
                        </button>
                        <p className="text-[11px] text-zen-ink-soft mt-4 leading-relaxed">
                            By continuing you agree to receive a one-time SMS. Msg
                            & data rates may apply.
                        </p>
                    </div>
                )}

                {step === "pin" && (
                    <div className="p-8 sm:p-10">
                        <p className="text-[11px] uppercase tracking-[0.28em] text-zen-olive mb-3 inline-flex items-center gap-1.5">
                            <Sparkles size={11} /> ZenCircle
                        </p>
                        <h2 className="font-serif font-light text-3xl text-zen-ink mb-2 leading-tight">
                            Enter your code.
                        </h2>
                        <p className="text-sm text-zen-ink-soft mb-7 leading-relaxed">
                            We just texted a 6-digit code to{" "}
                            <span className="text-zen-ink font-medium">
                                {phone.replace(
                                    /(\d{3})(\d{3})(\d{4})/,
                                    "($1) $2-$3"
                                )}
                            </span>
                            . It expires in 10 minutes.
                        </p>
                        <PinInput
                            value={pin}
                            onChange={setPin}
                            disabled={loading}
                            autoFocus
                        />
                        {error && (
                            <p
                                data-testid="loyalty-error"
                                className="text-sm text-red-700 mt-3"
                            >
                                {error}
                            </p>
                        )}
                        <button
                            onClick={verifyPin}
                            disabled={loading || pin.length !== 6}
                            data-testid="loyalty-verify-code"
                            className="mt-6 w-full bg-zen-ink text-zen-cream px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                        >
                            {loading ? "Verifying…" : "Show my points"}
                            {!loading && <ArrowRight size={14} />}
                        </button>
                        <button
                            onClick={() => {
                                setStep("phone");
                                setPin("");
                                setError(null);
                            }}
                            disabled={loading}
                            className="mt-3 w-full text-[11px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink transition-colors py-2"
                        >
                            Use a different number
                        </button>
                    </div>
                )}

                {step === "result" && result && (
                    <div className="p-8 sm:p-10 text-center">
                        <p className="text-[11px] uppercase tracking-[0.28em] text-zen-olive mb-4 inline-flex items-center gap-1.5">
                            <Sparkles size={11} /> ZenCircle balance
                        </p>
                        {result.enrolled === false ? (
                            <>
                                <h2 className="font-serif font-light text-3xl text-zen-ink mb-3 leading-tight">
                                    You&rsquo;re not in ZenCircle yet.
                                </h2>
                                <p className="text-sm text-zen-ink-soft mb-7 leading-relaxed">
                                    Earn points on every purchase. Join in-store or
                                    at checkout — it takes 30 seconds.
                                </p>
                            </>
                        ) : (
                            <>
                                <p
                                    data-testid="loyalty-points"
                                    className="font-serif font-light text-7xl sm:text-8xl text-zen-ink leading-none my-6 tracking-tight"
                                >
                                    {Number(result.points || 0).toLocaleString()}
                                </p>
                                <p className="text-sm text-zen-ink-soft">
                                    points in your wallet
                                </p>
                                {result.next_reward && (
                                    <p
                                        data-testid="loyalty-next-reward"
                                        className="text-xs text-zen-olive mt-4 px-4 py-2 inline-block border border-zen-olive/30 bg-zen-olive/5"
                                    >
                                        {typeof result.next_reward === "string"
                                            ? result.next_reward
                                            : `${result.next_reward.points_needed || "?"} more for "${result.next_reward.name || "your next reward"}"`}
                                    </p>
                                )}
                            </>
                        )}
                        <button
                            onClick={() => {
                                reset();
                                onClose();
                            }}
                            className="mt-8 w-full bg-zen-ink text-zen-cream px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-zen-olive-dark transition-colors inline-flex items-center justify-center gap-2"
                        >
                            Done
                        </button>
                        {result.enrolled !== false && (
                            <WalletPassButton phone={phone} />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
